import { useMutation } from '@tanstack/react-query';

import { User } from '@zep/types';

import { UseZepApiMutationOptions } from '../types/ZepApiQuery';
import { useZepApiClient } from '../ZepApiProvider';

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export function useRequestSignInCode(
  options?: UseZepApiMutationOptions<void, string>,
) {
  const client = useZepApiClient();
  return useMutation(async email => {
    await client.post('/v2/signin', {
      email,
    });
  }, options);
}

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export function useSignInWithCode(
  options?: UseZepApiMutationOptions<
    [string, User],
    { email: string; code: string; isApp?: boolean }
  >,
) {
  const client = useZepApiClient();
  return useMutation(async body => {
    const { email, code, isApp = false } = body;
    const { data } = await client.post('/v2/signin/confirm', {
      email,
      t: code,
      isApp,
    });
    return [data.loginToken, data.player] as [string, User];
  }, options);
}

/**
 * @deprecated API type 마이그레이션이 필요합니다. README의 API Type Safety 가이드를 참고하세요: docs/api-type-safety.md
 */
export function useSignInWithSns(
  options?: UseZepApiMutationOptions<
    [string, User],
    {
      snsType: 'google' | 'apple';
      email?: string;
      snsToken: string;
      isApp?: boolean;
    }
  >,
) {
  const client = useZepApiClient();
  return useMutation(async body => {
    const { snsType, email, snsToken, isApp = false } = body;
    const { data } = await client.post('/v2/signin/sns', {
      sns_type: snsType,
      email,
      sns_token: snsToken,
      isApp,
    });
    return [data.loginToken, data.player] as [string, User];
  }, options);
}

/**
 * SSO Exchange Token API
 * Auth Server에서 발급한 토큰을 ZEP 서버 인증으로 교환
 *
 * 토큰 전달 방식:
 * 1. Request Body (token 필드) - cross-domain 케이스에서 사용
 * 2. Query Parameter - fallback
 * 3. Cookie (ZEP_AUTH_SERVER_TOKEN) - same-domain 케이스
 *
 * @returns { loginToken, sessionKey, player }
 */
export interface SsoExchangeTokenResponse {
  loginToken: string;
  sessionKey: string;
  player: User;
}

export interface SsoExchangeTokenParams {
  token?: string; // cross-domain 케이스에서 query param으로 전달받은 토큰
}

export function useSsoExchangeToken(
  options?: UseZepApiMutationOptions<
    SsoExchangeTokenResponse,
    SsoExchangeTokenParams
  >,
) {
  const client = useZepApiClient();
  return useMutation(async (params?: SsoExchangeTokenParams) => {
    const { data } = await client.post('/v2/auth/exchange-token', {
      token: params?.token,
    });
    return {
      loginToken: data.loginToken,
      sessionKey: data.sessionKey,
      player: data.player,
    } as SsoExchangeTokenResponse;
  }, options);
}
